import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { HelpCircle, Search, MessageCircle, Phone, Mail, BookOpen, FileText } from 'lucide-react';
import { FAQSkeleton } from '../../components/shared/ClientPageSkeleton';
import { usePublicFAQs } from '../../hooks/usePublicFAQs';
import { siteSettingsService } from '../../services/siteSettingsService';
import { pageValue, sectionOrder, seedFromSettings } from '../../content/publicPages';
import { renderSections } from '../../content/sectionTone';
import { usePublicPage } from '../../hooks/usePublicPage';
import MarketingReveal from '../../components/home/MarketingReveal';
import {
  MarketingPage,
  MarketingMediaHero,
  MarketingSection,
  MarketingSplitIntro,
  MarketingFeatureGrid,
  MarketingPrimaryButton,
} from '../../components/home/MarketingPageChrome';

const HELP_HERO =
  'https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&w=1800&q=80';

const categoryIcons = {
  'getting-started': BookOpen,
  bookings: FileText,
  payments: FileText,
  account: HelpCircle,
  safety: HelpCircle,
};

const HelpCenter = () => {
  const [activeTab, setActiveTab] = useState('getting-started');
  const [searchQuery, setSearchQuery] = useState('');
  const { faqsByCategory, categories, isLoading, error } = usePublicFAQs();

  const { saved, defaults } = usePublicPage('help');
  const { data: settingsResponse } = useQuery({
    queryKey: ['siteSettings'],
    queryFn: () => siteSettingsService.get(),
    staleTime: 5 * 60 * 1000,
  });
  const settings = settingsResponse?.data || {};
  const seed = seedFromSettings('help', settings);
  const val = (key) => pageValue(saved, seed, defaults, key);
  const order = sectionOrder(saved, 'help');
  const supportEmail = settings.site_email || 'support@example.com';
  const supportPhone = settings.site_phone || '1-800-000-0000';

  const tabs = categories.map((cat) => ({
    ...cat,
    icon: categoryIcons[cat.id] || HelpCircle,
  }));

  const faqs = useMemo(() => {
    const list = faqsByCategory[activeTab] || [];
    if (!searchQuery.trim()) return list;
    const q = searchQuery.toLowerCase();
    return list.filter(
      (faq) =>
        (faq.question || '').toLowerCase().includes(q) ||
        (faq.answer || '').toLowerCase().includes(q)
    );
  }, [faqsByCategory, activeTab, searchQuery]);

  const cardIcons = [MessageCircle, Mail, Phone, HelpCircle];
  const contactOptions = (val('contact_cards') || []).map((card, index) => ({
    icon: cardIcons[index % cardIcons.length],
    title: card.title,
    description: card.body,
    href: card.href?.startsWith('mailto:') || card.href?.startsWith('tel:') ? card.href : undefined,
    to: card.href?.startsWith('/') ? card.href : undefined,
  }));

  const blocks = {
    hero: (
      <MarketingMediaHero
        key="hero"
        title={val('title')}
        subtitle={val('subtitle')}
        image={val('image') || HELP_HERO}
        height="sm"
        actions={
          <div className="relative mx-auto w-full max-w-lg">
            <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-m-ink-muted" />
            <input
              type="text"
              placeholder={val('search_placeholder') || 'Search help…'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-12 w-full rounded-full border-0 bg-white pl-11 pr-4 text-sm font-medium text-m-ink placeholder:text-m-ink-faint focus:outline-none focus:ring-2 focus:ring-white/40"
            />
          </div>
        }
      />
    ),
    intro: (
      <MarketingSection key="intro" tone="white">
        <MarketingSplitIntro title={val('intro_title')}>
          <p>{val('intro_body')}</p>
        </MarketingSplitIntro>
      </MarketingSection>
    ),
    faqs: (
      <MarketingSection key="faqs" tone="white" className="!pt-0 sm:!pt-0">
        <div className="mx-auto max-w-3xl">
          {tabs.length > 0 ? (
            <nav className="mb-8 flex gap-1 overflow-x-auto border-b border-m-border-light scrollbar-hide">
              {tabs.map((tab) => {
                const IconComponent = tab.icon;
                const active = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveTab(tab.id)}
                    className={`inline-flex shrink-0 items-center gap-2 border-b-2 px-3 py-3 text-sm font-medium transition-colors ${
                      active
                        ? 'border-m-ink text-m-ink'
                        : 'border-transparent text-m-ink-muted hover:text-m-ink'
                    }`}
                  >
                    <IconComponent className="h-4 w-4" />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          ) : null}

          <MarketingReveal>
            <h2 className="mb-2 text-xl font-bold tracking-tight" style={{ color: '#0a0a0a' }}>
              {val('faq_heading')}
            </h2>
          </MarketingReveal>

          {isLoading ? (
            <FAQSkeleton count={6} />
          ) : error ? (
            <p className="py-8 text-center text-sm text-red-600">
              Failed to load FAQs. Please try again later.
            </p>
          ) : faqs.length === 0 ? (
            <p className="py-8 text-center text-sm" style={{ color: '#6b6b6b' }}>
              No matching articles. Try another search or category.
            </p>
          ) : (
            <div className="mt-6 divide-y divide-m-border-light border-y border-m-border-light">
              {faqs.map((faq, index) => (
                <details key={faq.id || index} className="group py-5">
                  <summary
                    className="cursor-pointer list-none text-base font-semibold tracking-tight marker:content-none [&::-webkit-details-marker]:hidden"
                    style={{ color: '#0a0a0a' }}
                  >
                    <span className="flex items-start justify-between gap-4">
                      {faq.question}
                      <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center text-lg font-normal text-m-ink-muted transition-transform duration-300 group-open:rotate-45">
                        +
                      </span>
                    </span>
                  </summary>
                  <p className="mt-3 pr-8 text-[15px] leading-relaxed" style={{ color: '#6b6b6b' }}>
                    {faq.answer}
                  </p>
                </details>
              ))}
            </div>
          )}
        </div>
      </MarketingSection>
    ),
    contact: (
      <MarketingSection key="contact" tone="canvas">
        <div className="mx-auto max-w-6xl">
          <MarketingReveal className="mb-8 max-w-xl">
            <h2 className="marketing-section-title" style={{ color: '#0a0a0a' }}>
              {val('contact_title')}
            </h2>
          </MarketingReveal>
          <MarketingFeatureGrid items={contactOptions} />
        </div>
      </MarketingSection>
    ),
    closing: (
      <MarketingSection key="closing" tone="charcoal">
        <MarketingReveal className="mx-auto max-w-2xl text-center">
          <p className="mb-6 text-[1.05rem] leading-relaxed text-white/70">
            {val('closing_body')}{' '}
            <Link to={val('closing_link') || '/contact'} className="underline underline-offset-2 hover:text-white">
              {val('closing_link_label')}
            </Link>
            .
          </p>
          <MarketingPrimaryButton
            to={val('closing_button_link') || '/contact'}
            className="!bg-white !text-m-ink hover:!bg-white/90"
          >
            {val('closing_button')}
          </MarketingPrimaryButton>
        </MarketingReveal>
      </MarketingSection>
    ),
  };

  return <MarketingPage>{renderSections(order, blocks, saved)}</MarketingPage>;
};

export default HelpCenter;
